import type { SupabaseClient } from '@supabase/supabase-js';
import { getProductReviewFeatures } from './reviewFeatures.js';
import {
  getCommunityWorkflowFeatures,
  quickAddOwnedEquipment,
  type EquipmentQuickAddInput,
} from './communityWorkflows.js';
import {
  catalogQuerySchema,
  profileUpdateSchema,
  socialLinkSchema,
  ownedItemSchema,
  purchaseSchema,
  reviewSchema,
  postSchema,
  commentSchema,
  storySchema,
  topicSchema,
  replySchema,
  tradeSchema,
  messageSchema,
  reportSchema,
  settingsUpdateSchema,
  pageQuerySchema,
  gymUpdateSchema,
  catalogSubmissionSchema,
  catalogProductSchema,
  moderationCaseSchema,
  moderationSchema,
  roleAssignmentSchema,
  uuidSchema,
} from '@garagegymbros/contracts';
import type {
  Profile,
  PrivateAccountSettings,
  SocialLink,
  ProductSummary,
  Manufacturer,
  EquipmentCategory,
  OwnedItem,
  PurchaseRecord,
  ProductReview,
  Page,
  Gym,
  Post,
  Comment,
  Story,
  ForumCategory,
  ForumTopic,
  ForumReply,
  TradeListing,
  TradeStatus,
  Conversation,
  Message,
  Notification,
  ContentReport,
  AccountRequest,
  MuscleGroup,
  CatalogProductMetadata,
} from '@garagegymbros/contracts';
import { decodeCursor, nextCursor, escapeSearchTerm } from '@garagegymbros/domain';
import { unwrap, DataError } from './client.js';
interface PageOptions {
  limit?: number;
  cursor?: string;
}
/** All queries use caller-scoped Supabase. RLS is the final authority, including nested reads. */
export function createRepositories(client: SupabaseClient) {
  async function userId(): Promise<string> {
    const { data, error } = await client.auth.getUser();
    if (error || !data.user) throw new DataError('UNAUTHENTICATED', 'Sign in to continue.');
    return data.user.id;
  }
  async function list<T>(
    table: string,
    options: PageOptions = {},
    filter?: { field: string; value: unknown },
    select = '*',
  ): Promise<Page<T>> {
    const parsed = pageQuerySchema.parse(options),
      limit = parsed.limit,
      offset = decodeCursor(parsed.cursor);
    let query = client
      .from(table)
      .select(select)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(offset, offset + limit - 1);
    if (filter) query = query.eq(filter.field, filter.value);
    const items = unwrap<T[]>(await query);
    return { items, next_cursor: nextCursor(offset, items.length, limit) };
  }
  async function insert<T>(table: string, value: Record<string, unknown>): Promise<T> {
    return unwrap<T>(await client.from(table).insert(value).select().single());
  }
  async function update<T>(table: string, id: string, value: Record<string, unknown>): Promise<T> {
    uuidSchema.parse(id);
    return unwrap<T>(await client.from(table).update(value).eq('id', id).select().single());
  }
  async function remove(table: string, id: string) {
    uuidSchema.parse(id);
    unwrap(await client.from(table).delete().eq('id', id));
  }
  return {
    profiles: {
      async get(id: string) {
        return unwrap<Profile | null>(
          await client.from('profiles').select('*').eq('id', uuidSchema.parse(id)).maybeSingle(),
        );
      },
      async byUsername(username: string) {
        return unwrap<Profile | null>(
          await client
            .from('profiles')
            .select('*')
            .eq('username', username.toLowerCase())
            .maybeSingle(),
        );
      },
      async search(q: string, options: PageOptions = {}) {
        const limit = Math.min(options.limit ?? 24, 100);
        return unwrap<Profile[]>(
          await client
            .from('profiles')
            .select('*')
            .ilike('username', `%${escapeSearchTerm(q)}%`)
            .order('username')
            .limit(limit),
        );
      },
      async update(input: unknown) {
        return update<Profile>('profiles', await userId(), profileUpdateSchema.parse(input));
      },
    },
    settings: {
      async get() {
        return unwrap<PrivateAccountSettings | null>(
          await client
            .from('private_account_settings')
            .select('*')
            .eq('user_id', await userId())
            .maybeSingle(),
        );
      },
      async update(
        input: Pick<
          PrivateAccountSettings,
          'first_name' | 'last_name' | 'notification_preferences' | 'catalog_view'
        >,
      ) {
        return unwrap<PrivateAccountSettings>(
          await client
            .from('private_account_settings')
            .update(settingsUpdateSchema.parse(input))
            .eq('user_id', await userId())
            .select()
            .single(),
        );
      },
    },
    socialLinks: {
      async list(user: string) {
        return unwrap<SocialLink[]>(
          await client
            .from('social_links')
            .select('*')
            .eq('user_id', uuidSchema.parse(user))
            .order('position')
            .order('id'),
        );
      },
      async add(input: unknown) {
        return insert<SocialLink>('social_links', {
          ...socialLinkSchema.parse(input),
          user_id: await userId(),
        });
      },
      async update(id: string, input: unknown) {
        return update<SocialLink>('social_links', id, socialLinkSchema.parse(input));
      },
      async remove(id: string) {
        return remove('social_links', id);
      },
    },
    catalog: {
      async manufacturers() {
        return unwrap<Manufacturer[]>(await client.from('manufacturers').select('*').order('name'));
      },
      async categories() {
        return unwrap<EquipmentCategory[]>(
          await client.from('equipment_categories').select('*').order('name'),
        );
      },
      async muscles() {
        return unwrap<MuscleGroup[]>(
          await client.from('muscle_groups').select('slug,name').order('name'),
        );
      },
      async metadata(ids: string[]) {
        if (ids.length > 50)
          throw new DataError('VALIDATION', 'Request at most 50 catalog products.');
        return unwrap<CatalogProductMetadata[]>(
          await client.rpc('catalog_product_metadata', {
            product_ids: [...new Set(ids.map((id) => uuidSchema.parse(id)))],
          }),
        );
      },
      async search(input: unknown = {}): Promise<Page<ProductSummary>> {
        const q = catalogQuerySchema.parse(input),
          offset = decodeCursor(q.cursor);
        const items = unwrap<ProductSummary[]>(
          await client.rpc('search_catalog', {
            query_text: q.q,
            manufacturer: q.manufacturer_id ?? null,
            category: q.category_id ?? null,
            sort_by: q.sort === 'price' ? 'price_low' : q.sort,
            min_rating: q.min_rating ?? null,
            offer_currency: q.currency,
            page_limit: q.limit,
            page_offset: offset,
            min_price: q.min_price ?? null,
            max_price: q.max_price ?? null,
            has_3d: q.has_3d ?? null,
            muscle_slugs: q.muscle_slugs,
            max_width_mm: q.max_width_mm ?? null,
            max_depth_mm: q.max_depth_mm ?? null,
            max_height_mm: q.max_height_mm ?? null,
          }),
        );
        return {
          items,
          next_cursor: nextCursor(offset, items.length, q.limit),
          total: items[0]?.total_count ?? 0,
        };
      },
      async get(slugOrId: string) {
        let query = client
          .from('products')
          .select(
            '*,manufacturers(*),equipment_categories(*),product_variants(*),product_offers(*)',
          );
        query = uuidSchema.safeParse(slugOrId).success
          ? query.eq('id', slugOrId)
          : query.eq('slug', slugOrId);
        const product = unwrap<ProductSummary | null>(await query.maybeSingle());
        if (!product) return null;
        const metadata = unwrap<CatalogProductMetadata[]>(
          await client.rpc('catalog_product_metadata', { product_ids: [product.id] }),
        )[0];
        return metadata
          ? {
              ...product,
              dimensions: metadata.dimensions,
              muscle_groups: metadata.muscle_groups,
              primary_image: metadata.primary_image,
            }
          : product;
      },
    },
    gyms: {
      async mine() {
        return unwrap<Gym[]>(
          await client
            .from('gyms')
            .select('*')
            .eq('owner_id', await userId())
            .order('created_at'),
        );
      },
      async update(id: string, input: unknown) {
        return update<Gym>('gyms', id, gymUpdateSchema.parse(input));
      },
      async remove(id: string) {
        return remove('gyms', id);
      },
      async create(name: string, visibility: 'public' | 'followers' | 'private' = 'private') {
        if (!name.trim() || name.length > 100)
          throw new Error('Name your gym using up to 100 characters.');
        return insert<Gym>('gyms', {
          owner_id: await userId(),
          name: name.trim(),
          description: '',
          visibility,
          status: 'active',
        });
      },
    },
    inventory: {
      async quickAdd(input: Omit<EquipmentQuickAddInput, 'expectedOwnerId'>) {
        return quickAddOwnedEquipment(client, { ...input, expectedOwnerId: await userId() });
      },
      async list(owner: string, options: PageOptions = {}) {
        return list<OwnedItem>('owned_items', options, {
          field: 'owner_id',
          value: uuidSchema.parse(owner),
        });
      },
      async add(input: unknown) {
        return insert<OwnedItem>('owned_items', {
          ...ownedItemSchema.parse(input),
          owner_id: await userId(),
        });
      },
      async update(id: string, input: unknown) {
        return update<OwnedItem>('owned_items', id, ownedItemSchema.parse(input));
      },
      async remove(id: string) {
        return remove('owned_items', id);
      },
      async purchases(itemId: string) {
        return unwrap<PurchaseRecord[]>(
          await client
            .from('owned_item_purchase_records')
            .select('*')
            .eq('item_id', uuidSchema.parse(itemId))
            .eq('owner_id', await userId())
            .order('created_at', { ascending: false }),
        );
      },
      async updatePurchase(id: string, input: unknown) {
        return update<PurchaseRecord>(
          'owned_item_purchase_records',
          id,
          purchaseSchema.parse(input),
        );
      },
      async removePurchase(id: string) {
        return remove('owned_item_purchase_records', id);
      },
      async submitCatalogCorrection(input: unknown, expectedOwnerId?: string) {
        const inputRow = catalogSubmissionSchema.parse(input);
        const owner = await userId();
        if (expectedOwnerId && owner !== uuidSchema.parse(expectedOwnerId))
          throw new DataError(
            'ACCOUNT_CHANGED',
            'Your account changed. Reopen the suggestion form.',
          );
        return insert('catalog_submissions', { ...inputRow, user_id: owner });
      },
      async addPurchase(itemId: string, input: unknown) {
        return insert<PurchaseRecord>('owned_item_purchase_records', {
          ...purchaseSchema.parse(input),
          item_id: uuidSchema.parse(itemId),
          owner_id: await userId(),
        });
      },
    },
    reviews: {
      async list(productId: string, options: PageOptions = {}) {
        return list<ProductReview>('product_reviews', options, {
          field: 'product_id',
          value: uuidSchema.parse(productId),
        });
      },
      async save(input: unknown) {
        const value = reviewSchema.parse(input),
          user = await userId();
        if (!Number.isInteger(value.rating) && !(await getProductReviewFeatures(client)).halfStars)
          throw new Error(
            'Half-star review saving is awaiting backend activation. Whole-star reviews remain available.',
          );
        const existing = unwrap<{ id: string } | null>(
          await client
            .from('product_reviews')
            .select('id')
            .eq('user_id', user)
            .eq('product_id', value.product_id)
            .maybeSingle(),
        );
        if (existing) {
          const { product_id, ...changes } = value;
          return update<ProductReview>('product_reviews', existing.id, changes);
        }
        return insert<ProductReview>('product_reviews', { ...value, user_id: user });
      },
      async remove(id: string) {
        return remove('product_reviews', id);
      },
      async helpful(reviewId: string, enabled: boolean) {
        const user = await userId();
        if (enabled)
          unwrap(
            await client
              .from('review_helpful_votes')
              .upsert(
                { review_id: uuidSchema.parse(reviewId), user_id: user },
                { onConflict: 'review_id,user_id', ignoreDuplicates: true },
              ),
          );
        else
          unwrap(
            await client
              .from('review_helpful_votes')
              .delete()
              .eq('review_id', reviewId)
              .eq('user_id', user),
          );
      },
    },
    feed: {
      async list(
        options: PageOptions = {},
        mode: 'following' | 'discover' = 'following',
      ): Promise<Page<Post>> {
        const parsed = pageQuerySchema.parse(options),
          offset = decodeCursor(parsed.cursor),
          user = await userId();
        const muted = unwrap<Array<{ muted_user_id: string }>>(
          await client.from('mutes').select('muted_user_id').eq('user_id', user),
        );
        let query = client
          .from('posts')
          .select('*')
          .order('created_at', { ascending: false })
          .order('id', { ascending: false });
        if (mode === 'following') {
          const following = unwrap<Array<{ following_id: string }>>(
            await client
              .from('follows')
              .select('following_id')
              .eq('follower_id', user)
              .eq('status', 'accepted'),
          );
          const authors = following.map((item) => item.following_id);
          if (!authors.length) return { items: [], next_cursor: null };
          query = query.in('user_id', authors);
        }
        for (const item of muted) query = query.neq('user_id', item.muted_user_id);
        const items = unwrap<Post[]>(await query.range(offset, offset + parsed.limit - 1));
        return { items, next_cursor: nextCursor(offset, items.length, parsed.limit) };
      },
      async create(input: unknown) {
        return insert<Post>('posts', { ...postSchema.parse(input), user_id: await userId() });
      },
      async update(id: string, input: unknown) {
        return update<Post>('posts', id, postSchema.parse(input));
      },
      async remove(id: string) {
        return remove('posts', id);
      },
      async comments(postId: string, options: PageOptions = {}) {
        return list<Comment>('post_comments', options, {
          field: 'post_id',
          value: uuidSchema.parse(postId),
        });
      },
      async editComment(id: string, body: string) {
        const parsed = commentSchema.shape.body.parse(body);
        return update<Comment>('post_comments', id, { body: parsed });
      },
      async removeComment(id: string) {
        return remove('post_comments', id);
      },
      async tagEquipment(
        postId: string,
        target: { product_id: string } | { owned_item_id: string },
      ) {
        return insert('post_equipment_tags', {
          post_id: uuidSchema.parse(postId),
          ...('product_id' in target
            ? { product_id: uuidSchema.parse(target.product_id) }
            : { owned_item_id: uuidSchema.parse(target.owned_item_id) }),
        });
      },
      async untagEquipment(id: string) {
        return remove('post_equipment_tags', id);
      },
      async comment(input: unknown) {
        return insert<Comment>('post_comments', {
          ...commentSchema.parse(input),
          user_id: await userId(),
        });
      },
      async react(postId: string, enabled: boolean) {
        const user = await userId();
        if (enabled)
          unwrap(
            await client
              .from('post_reactions')
              .upsert(
                { post_id: uuidSchema.parse(postId), user_id: user },
                { onConflict: 'post_id,user_id', ignoreDuplicates: true },
              ),
          );
        else
          unwrap(
            await client.from('post_reactions').delete().eq('post_id', postId).eq('user_id', user),
          );
      },
    },
    stories: {
      async active() {
        return unwrap<Story[]>(
          await client
            .from('stories')
            .select('*')
            .gt('expires_at', new Date().toISOString())
            .order('created_at', { ascending: false })
            .limit(100),
        );
      },
      async create(input: unknown) {
        const value = storySchema.parse(input),
          user = await userId();
        if (
          value.duration_hours === 48 &&
          !(await getCommunityWorkflowFeatures(client)).flexDurationHours.includes(48)
        )
          throw new Error('48-hour Flexes are awaiting backend activation.');
        return insert<Story>('stories', { ...value, user_id: user });
      },
      async extend(id: string) {
        return unwrap<Story>(
          await client.rpc('extend_flex_duration', {
            p_story_id: uuidSchema.parse(id),
            p_expected_author_id: await userId(),
          }),
        );
      },
      async remove(id: string) {
        return remove('stories', id);
      },
      async respond(storyId: string, body: string) {
        const parsed = commentSchema.shape.body.parse(body);
        return insert('story_responses', {
          story_id: uuidSchema.parse(storyId),
          user_id: await userId(),
          kind: 'reply',
          body: parsed,
        });
      },
      async react(storyId: string, enabled = true) {
        const user = await userId();
        uuidSchema.parse(storyId);
        if (!enabled) {
          unwrap(
            await client
              .from('story_responses')
              .delete()
              .eq('story_id', storyId)
              .eq('user_id', user)
              .eq('kind', 'dumbbell'),
          );
          return null;
        }
        const result = await client
          .from('story_responses')
          .insert({ story_id: storyId, user_id: user, kind: 'dumbbell' })
          .select()
          .single();
        if (result.error?.code === '23505') return null;
        return unwrap(result);
      },
      async viewers(storyId: string) {
        return unwrap<Array<{ story_id: string; user_id: string; viewed_at: string }>>(
          await client
            .from('story_views')
            .select('*')
            .eq('story_id', uuidSchema.parse(storyId))
            .order('viewed_at', { ascending: false })
            .limit(100),
        );
      },
      async viewed(storyId: string) {
        unwrap(
          await client
            .from('story_views')
            .upsert(
              { story_id: uuidSchema.parse(storyId), user_id: await userId() },
              { onConflict: 'story_id,user_id', ignoreDuplicates: true },
            ),
        );
      },
    },
    relationships: {
      async follow(target: string) {
        return insert('follows', {
          follower_id: await userId(),
          following_id: uuidSchema.parse(target),
        });
      },
      async following(user: string) {
        return unwrap<Array<{ follower_id: string; following_id: string; status: string }>>(
          await client
            .from('follows')
            .select('*')
            .eq('follower_id', uuidSchema.parse(user))
            .order('created_at', { ascending: false })
            .limit(100),
        );
      },
      async followers(user: string) {
        return unwrap<Array<{ follower_id: string; following_id: string; status: string }>>(
          await client
            .from('follows')
            .select('*')
            .eq('following_id', uuidSchema.parse(user))
            .order('created_at', { ascending: false })
            .limit(100),
        );
      },
      async blocked() {
        return unwrap<Array<{ user_id: string; blocked_user_id: string }>>(
          await client
            .from('blocks')
            .select('*')
            .eq('user_id', await userId()),
        );
      },
      async muted() {
        return unwrap<Array<{ user_id: string; muted_user_id: string }>>(
          await client
            .from('mutes')
            .select('*')
            .eq('user_id', await userId()),
        );
      },
      async unfollow(target: string) {
        unwrap(
          await client
            .from('follows')
            .delete()
            .eq('follower_id', await userId())
            .eq('following_id', uuidSchema.parse(target)),
        );
      },
      async respond(requester: string, accept: boolean) {
        const user = await userId();
        if (accept)
          unwrap(
            await client
              .from('follows')
              .update({ status: 'accepted' })
              .eq('follower_id', uuidSchema.parse(requester))
              .eq('following_id', user),
          );
        else
          unwrap(
            await client
              .from('follows')
              .delete()
              .eq('follower_id', uuidSchema.parse(requester))
              .eq('following_id', user),
          );
      },
      async block(target: string, enabled: boolean) {
        const user = await userId();
        if (enabled)
          unwrap(
            await client
              .from('blocks')
              .upsert(
                { user_id: user, blocked_user_id: uuidSchema.parse(target) },
                { onConflict: 'user_id,blocked_user_id', ignoreDuplicates: true },
              ),
          );
        else
          unwrap(
            await client
              .from('blocks')
              .delete()
              .eq('user_id', user)
              .eq('blocked_user_id', uuidSchema.parse(target)),
          );
      },
      async mute(target: string, enabled: boolean) {
        const user = await userId();
        if (enabled)
          unwrap(
            await client
              .from('mutes')
              .upsert(
                { user_id: user, muted_user_id: uuidSchema.parse(target) },
                { onConflict: 'user_id,muted_user_id', ignoreDuplicates: true },
              ),
          );
        else
          unwrap(
            await client
              .from('mutes')
              .delete()
              .eq('user_id', user)
              .eq('muted_user_id', uuidSchema.parse(target)),
          );
      },
    },
    forum: {
      async categories() {
        return unwrap<ForumCategory[]>(
          await client.from('forum_categories').select('*').order('position'),
        );
      },
      async search(query: string, options: PageOptions = {}) {
        const page = pageQuerySchema.parse(options),
          offset = decodeCursor(page.cursor),
          term = escapeSearchTerm(query);
        const items = unwrap<ForumTopic[]>(
          await client
            .from('forum_topics')
            .select('*')
            .or(`title.ilike.%${term}%,body.ilike.%${term}%`)
            .order('created_at', { ascending: false })
            .order('id', { ascending: false })
            .range(offset, offset + page.limit - 1),
        );
        return { items, next_cursor: nextCursor(offset, items.length, page.limit) };
      },
      async topics(options: PageOptions = {}, categoryId?: string) {
        return list<ForumTopic>(
          'forum_topics',
          options,
          categoryId ? { field: 'category_id', value: uuidSchema.parse(categoryId) } : undefined,
        );
      },
      async createTopic(input: unknown) {
        return insert<ForumTopic>('forum_topics', {
          ...topicSchema.parse(input),
          user_id: await userId(),
        });
      },
      async replies(topic: string, options: PageOptions = {}) {
        return list<ForumReply>('forum_replies', options, {
          field: 'topic_id',
          value: uuidSchema.parse(topic),
        });
      },
      async reply(input: unknown) {
        return insert<ForumReply>('forum_replies', {
          ...replySchema.parse(input),
          user_id: await userId(),
        });
      },
      async editTopic(id: string, input: unknown) {
        const { kind, ...changes } = topicSchema.parse(input);
        return update<ForumTopic>('forum_topics', id, changes);
      },
      async removeTopic(id: string) {
        return remove('forum_topics', id);
      },
      async editReply(id: string, body: string) {
        return update<ForumReply>('forum_replies', id, {
          body: replySchema.shape.body.parse(body),
        });
      },
      async removeReply(id: string) {
        return remove('forum_replies', id);
      },
      async acceptAnswer(topic: string, replyId: string | null) {
        return unwrap(
          await client.rpc('accept_forum_answer', {
            topic_id: uuidSchema.parse(topic),
            reply_id: replyId ? uuidSchema.parse(replyId) : null,
          }),
        );
      },
      async subscribe(topic: string, enabled: boolean) {
        const user = await userId();
        if (enabled)
          unwrap(
            await client
              .from('forum_subscriptions')
              .upsert(
                { topic_id: uuidSchema.parse(topic), user_id: user },
                { onConflict: 'topic_id,user_id', ignoreDuplicates: true },
              ),
          );
        else
          unwrap(
            await client
              .from('forum_subscriptions')
              .delete()
              .eq('topic_id', uuidSchema.parse(topic))
              .eq('user_id', user),
          );
      },
    },
    trade: {
      async list(
        options: PageOptions = {},
        filters: { q?: string; location?: string; status?: TradeStatus } = {},
      ) {
        const page = pageQuerySchema.parse(options),
          offset = decodeCursor(page.cursor);
        let query = client
          .from('trade_listings')
          .select('*')
          .order('created_at', { ascending: false })
          .order('id', { ascending: false });
        if (filters.q) query = query.ilike('title', `%${escapeSearchTerm(filters.q)}%`);
        if (filters.location)
          query = query.ilike('approximate_location', `%${escapeSearchTerm(filters.location)}%`);
        if (filters.status) query = query.eq('status', filters.status);
        const items = unwrap<TradeListing[]>(await query.range(offset, offset + page.limit - 1));
        return { items, next_cursor: nextCursor(offset, items.length, page.limit) };
      },
      async create(input: unknown) {
        return insert<TradeListing>('trade_listings', {
          ...tradeSchema.parse(input),
          user_id: await userId(),
        });
      },
      async transition(id: string, status: TradeStatus) {
        return unwrap<void>(
          await client.rpc('transition_trade', {
            listing_id: uuidSchema.parse(id),
            new_status: status,
          }),
        );
      },
    },
    messages: {
      async conversations(options: PageOptions = {}) {
        return list<Conversation>('conversations', options);
      },
      async request(other: string) {
        return unwrap<string>(
          await client.rpc('request_conversation', { other_user: uuidSchema.parse(other) }),
        );
      },
      async respond(id: string, accept: boolean) {
        return unwrap(
          await client.rpc('respond_conversation', {
            conversation_id: uuidSchema.parse(id),
            accept,
          }),
        );
      },
      async list(conversation: string, options: PageOptions = {}) {
        return list<Message>('messages', options, {
          field: 'conversation_id',
          value: uuidSchema.parse(conversation),
        });
      },
      async send(input: unknown) {
        const value = messageSchema.parse(input);
        return unwrap<Message>(
          await client.rpc('send_message', {
            conversation_id: value.conversation_id,
            body: value.body,
            client_nonce: value.client_nonce ?? crypto.randomUUID(),
          }),
        );
      },
      async markRead(conversation: string) {
        unwrap(
          await client
            .from('conversation_participants')
            .update({ last_read_at: new Date().toISOString() })
            .eq('conversation_id', uuidSchema.parse(conversation))
            .eq('user_id', await userId()),
        );
      },
      async hide(conversation: string) {
        unwrap(
          await client
            .from('conversation_participants')
            .update({ hidden_at: new Date().toISOString() })
            .eq('conversation_id', uuidSchema.parse(conversation))
            .eq('user_id', await userId()),
        );
      },
    },
    notifications: {
      async list(options: PageOptions = {}) {
        return list<Notification>('notifications', options, {
          field: 'user_id',
          value: await userId(),
        });
      },
      async markRead(id: string) {
        return update<Notification>('notifications', id, { read_at: new Date().toISOString() });
      },
      async markAllRead() {
        unwrap(
          await client
            .from('notifications')
            .update({ read_at: new Date().toISOString() })
            .eq('user_id', await userId())
            .is('read_at', null),
        );
      },
    },
    reports: {
      async create(input: unknown) {
        const value = reportSchema.parse(input);
        return insert<ContentReport>('reports', {
          target_type: value.target_type,
          target_id: value.target_id,
          reason: [value.reason, value.details].filter(Boolean).join(': ').slice(0, 2000),
          user_id: await userId(),
        });
      },
      async appeal(auditEvent: string, reason: string) {
        if (reason.trim().length < 10 || reason.length > 4000)
          throw new Error('Explain your appeal in 10 to 4,000 characters.');
        return insert('moderation_appeals', {
          user_id: await userId(),
          audit_event_id: uuidSchema.parse(auditEvent),
          reason: reason.trim(),
        });
      },
      async mine(options: PageOptions = {}) {
        return list<ContentReport>('reports', options, { field: 'user_id', value: await userId() });
      },
    },
    account: {
      async requests() {
        return unwrap<AccountRequest[]>(
          await client
            .from('account_requests')
            .select('*')
            .eq('user_id', await userId())
            .order('requested_at', { ascending: false }),
        );
      },
      async request(kind: 'export' | 'deletion') {
        return unwrap<AccountRequest>(
          await client.rpc('request_account_action', { action_type: kind }),
        );
      },
      async cancel(requestId: string) {
        return unwrap<void>(
          await client.rpc('cancel_account_request', { request_id: uuidSchema.parse(requestId) }),
        );
      },
      async exportData() {
        return unwrap<Record<string, unknown>>(await client.rpc('export_my_data'));
      },
      async deactivate() {
        return unwrap<void>(await client.rpc('deactivate_account'));
      },
      async reactivate() {
        return unwrap<void>(await client.rpc('reactivate_account'));
      },
    },
    admin: {
      async assignRole(input: unknown) {
        const value = roleAssignmentSchema.parse(input);
        return unwrap<void>(await client.rpc('set_account_role', value));
      },
      async moderate(input: unknown) {
        return unwrap<void>(await client.rpc('moderate_content', moderationSchema.parse(input)));
      },
      async memberDirectory(search = '', options: PageOptions = {}) {
        const page = pageQuerySchema.parse(options);
        return unwrap(
          await client.rpc('admin_member_directory', {
            search_text: search.slice(0, 100),
            page_limit: page.limit,
            page_offset: decodeCursor(page.cursor),
          }),
        );
      },
      async publishImport(jobId: string, reason: string) {
        if (reason.trim().length < 10 || reason.length > 1000)
          throw new Error('Record a reason of 10 to 1,000 characters.');
        return unwrap(
          await client.rpc('publish_catalog_import', {
            job_id: uuidSchema.parse(jobId),
            reason: reason.trim(),
          }),
        );
      },
      async mergeProducts(source: string, target: string, reason: string) {
        if (reason.trim().length < 10 || reason.length > 1000)
          throw new Error('Record a reason of 10 to 1,000 characters.');
        return unwrap(
          await client.rpc('merge_products', {
            source_product: uuidSchema.parse(source),
            target_product: uuidSchema.parse(target),
            reason: reason.trim(),
          }),
        );
      },
      async myModerationActions() {
        return unwrap(await client.rpc('my_moderation_actions'));
      },
      async readReportedMessage(reportId: string, reason: string) {
        if (reason.trim().length < 10 || reason.length > 1000)
          throw new Error('Record a reason of 10 to 1,000 characters.');
        return unwrap(
          await client.rpc('read_reported_message', {
            report_id: uuidSchema.parse(reportId),
            reason: reason.trim(),
          }),
        );
      },
      async role() {
        return unwrap<{ role: string } | null>(
          await client
            .from('staff_roles')
            .select('role')
            .eq('user_id', await userId())
            .maybeSingle(),
        );
      },
      async reports(options: PageOptions = {}) {
        return list<ContentReport>('reports', options);
      },
      async audit(options: PageOptions = {}) {
        return list('audit_events', options);
      },
      async cases(options: PageOptions = {}) {
        return list('moderation_cases', options);
      },
      async createCase(input: unknown) {
        return insert('moderation_cases', moderationCaseSchema.parse(input));
      },
      async caseNote(caseId: string, body: string) {
        if (body.trim().length < 1 || body.length > 4000)
          throw new Error('Enter a case note up to 4,000 characters.');
        return insert('moderation_case_notes', {
          case_id: uuidSchema.parse(caseId),
          user_id: await userId(),
          body: body.trim(),
        });
      },
      async sources() {
        return unwrap(await client.from('catalog_sources').select('*').order('name'));
      },
      async imports(options: PageOptions = {}) {
        return list('import_jobs', options);
      },
      async createProduct(input: unknown) {
        return insert<ProductSummary>('products', catalogProductSchema.parse(input));
      },
      async updateProduct(id: string, input: unknown) {
        return update<ProductSummary>('products', id, catalogProductSchema.parse(input));
      },
      async submissions(options: PageOptions = {}) {
        return list('catalog_submissions', options);
      },
    },
    realtime: {
      subscribe(
        table: 'posts' | 'messages' | 'notifications',
        onChange: () => void,
        filter?: string,
      ) {
        const channel = client
          .channel(`ggb:${table}:${crypto.randomUUID()}`)
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) },
            onChange,
          )
          .subscribe();
        return () => {
          void client.removeChannel(channel);
        };
      },
    },
  };
}
export type Repositories = ReturnType<typeof createRepositories>;
